import { useState } from 'react';
import GlobalConfigViewer from '../components/dev/GlobalConfigViewer';
import GlobalDumpVaultViewer from '../components/dev/GlobalDumpVaultViewer';
import CurrentGameStateViewer from '../components/dev/CurrentGameStateViewer';
import CurrentReserveVaultViewer from '../components/dev/CurrentReserveVaultViewer';
import { useSolanaLive } from '../hooks/useSolanaLive';
import { useUserBalances } from '../hooks/useUserBalances';

const tabs = [
  {
    id: 'global-config',
    label: 'Global Config',
    title: 'Global Config Subscription',
    description: 'This tab fetches the global_config PDA once and keeps the decoded account data live on screen.',
  },
  {
    id: 'global-dump-vault',
    label: 'Global Dump Vault',
    title: 'Global Dump Vault',
    description: 'This tab subscribes directly to the global_dump_vault account and updates when lamports change.',
  },
  {
    id: 'game-state',
    label: 'Game State',
    title: 'Game State',
    description: 'This tab follows global_config.active_game and switches to the current round gameState account automatically.',
  },
  {
    id: 'current-reserve-vault',
    label: 'Current Reserve Vault',
    title: 'Current Reserve Vault',
    description: 'This tab follows currentGameState.reserve_vault and switches to the active round reserve vault automatically.',
  },
  {
    id: 'user-balances',
    label: 'User Balances',
    title: 'User Balance Checks',
    description: 'This tab tests the wallet SOL balance and current round token balance helpers using the root app wallet state.',
  },
];

function UserBalancesViewer({ phantomInstalled, walletConnected, walletPublicKey }) {
  const { currentGameState } = useSolanaLive();
  const {
    error,
    loading,
    refreshBalances,
    roundTokenAmountAtomic,
    roundTokenAta,
    roundTokenBalance,
    solBalance,
    solLamports,
  } = useUserBalances(walletPublicKey);

  return (
    <div className="global-config-viewer">
      <div className="global-config-viewer__metaGrid">
        <div className="surface global-config-viewer__metaCard">
          <span className="label">Wallet connected</span>
          <strong>{walletConnected ? 'Yes' : 'No'}</strong>
        </div>

        <div className="surface global-config-viewer__metaCard">
          <span className="label">Phantom installed</span>
          <strong>{phantomInstalled ? 'Yes' : 'No'}</strong>
        </div>

        <div className="surface global-config-viewer__metaCard">
          <span className="label">Wallet public key</span>
          <strong className="global-config-viewer__mono">{walletPublicKey || 'No wallet connected'}</strong>
        </div>

        <div className="surface global-config-viewer__metaCard">
          <span className="label">Current round mint</span>
          <strong className="global-config-viewer__mono">{currentGameState?.mint || 'No active round mint'}</strong>
        </div>

        <div className="surface global-config-viewer__metaCard">
          <span className="label">Current round decimals</span>
          <strong>{currentGameState?.decimals ?? 'N/A'}</strong>
        </div>

        <div className="surface global-config-viewer__metaCard">
          <span className="label">Loading</span>
          <strong>{loading ? 'Loading' : 'Ready'}</strong>
        </div>

        <div className="surface global-config-viewer__metaCard">
          <span className="label">Wallet SOL balance</span>
          <strong>{walletConnected ? `${solBalance} SOL` : 'Connect wallet to test'}</strong>
        </div>

        <div className="surface global-config-viewer__metaCard">
          <span className="label">Wallet SOL lamports</span>
          <strong>{walletConnected ? String(solLamports) : '0'}</strong>
        </div>

        <div className="surface global-config-viewer__metaCard">
          <span className="label">Round token balance</span>
          <strong>{walletConnected ? roundTokenBalance : 'Connect wallet to test'}</strong>
        </div>

        <div className="surface global-config-viewer__metaCard">
          <span className="label">Round token atomic</span>
          <strong>{walletConnected ? roundTokenAmountAtomic : '0'}</strong>
        </div>

        <div className="surface global-config-viewer__metaCard">
          <span className="label">Round token ATA</span>
          <strong className="global-config-viewer__mono">
            {walletConnected ? roundTokenAta || 'No ATA for current mint' : 'Connect wallet to test'}
          </strong>
        </div>
      </div>

      <div className="surface global-config-viewer__jsonCard">
        <div className="global-config-viewer__jsonHeader">
          <div>
            <p className="eyebrow">Balance Test</p>
            <h2>user balance hook output</h2>
          </div>
          {error ? <span className="status-pill status-pill--warning">{error}</span> : null}
        </div>

        <pre className="global-config-viewer__json">
          {JSON.stringify(
            {
              phantomInstalled,
              walletConnected,
              walletPublicKey,
              currentRoundMint: currentGameState?.mint || '',
              currentRoundDecimals: currentGameState?.decimals ?? null,
              solBalance,
              solLamports,
              roundTokenBalance,
              roundTokenAmountAtomic,
              roundTokenAta,
              loading,
              error,
            },
            null,
            2
          )}
        </pre>

        <button type="button" className="ghost-button" onClick={refreshBalances} disabled={loading}>
          {loading ? 'Refreshing...' : 'Refresh Balances'}
        </button>
      </div>
    </div>
  );
}

function renderTabContent(activeTab, walletProps) {
  if (activeTab === 'global-config') {
    return <GlobalConfigViewer />;
  }

  if (activeTab === 'global-dump-vault') {
    return <GlobalDumpVaultViewer />;
  }

  if (activeTab === 'game-state') {
    return <CurrentGameStateViewer />;
  }

  if (activeTab === 'user-balances') {
    return <UserBalancesViewer {...walletProps} />;
  }

  return <CurrentReserveVaultViewer />;
}

function getTabStatus(tabId, liveState) {
  if (tabId === 'global-config') {
    if (liveState.loading.globalConfig) {
      return 'Loading';
    }

    return liveState.subscribed.globalConfig ? 'Active' : 'Not active';
  }

  if (tabId === 'global-dump-vault') {
    if (liveState.loading.dumpVault) {
      return 'Loading';
    }

    return liveState.subscribed.dumpVault ? 'Active' : 'Not active';
  }

  if (!liveState.currentGamePubkey) {
    return 'No active game';
  }

  if (liveState.loading.currentGameState) {
    return 'Switching';
  }

  if (tabId === 'current-reserve-vault') {
    if (!liveState.currentReserveVaultPubkey) {
      return 'Waiting for reserve vault';
    }

    if (liveState.loading.currentReserveVault) {
      return 'Loading';
    }

    return liveState.subscribed.currentReserveVault ? 'Active' : 'Not active';
  }

  if (tabId === 'user-balances') {
    return 'Manual refresh';
  }

  return liveState.subscribed.currentGameState ? 'Active' : 'Not active';
}

function Subscriptions({ phantomInstalled, walletConnected, walletPublicKey }) {
  const [activeTab, setActiveTab] = useState('global-config');
  const liveState = useSolanaLive();
  const activeTabConfig = tabs.find((tab) => tab.id === activeTab) || tabs[0];

  return (
    <section className="page-section secondary-page subscriptions-page">
      <div className="secondary-page__hero">
        <article className="panel page-copy secondary-page__lead">
          <p className="eyebrow">Dev</p>
          <h1>Subscriptions</h1>
          <p>
            This page groups subscription containers in one place. The global_config and
            global_dump_vault tabs are live right now, the current round tabs follow the active game,
            and the user balance tab lets you manually test wallet balance reads.
          </p>
        </article>

        <aside className="page-card secondary-page__statusCard">
          <p className="kicker">Current Tab</p>
          <h3>{activeTabConfig.title}</h3>
          <p>{activeTabConfig.description}</p>
          <div className="surface">
            <span className="label">Live status</span>
            <strong>{getTabStatus(activeTabConfig.id, liveState)}</strong>
          </div>
        </aside>
      </div>

      <article className="panel subscriptions-page__panel">
        <div className="subscriptions-page__tabs" role="tablist" aria-label="Subscription tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              className={
                activeTab === tab.id
                  ? 'subscriptions-page__tab subscriptions-page__tab--active'
                  : 'subscriptions-page__tab'
              }
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="subscriptions-page__content">
          <div className="subscriptions-page__contentHeader">
            <div>
              <p className="eyebrow">Subscription View</p>
              <h2>{activeTabConfig.title}</h2>
            </div>
            <p className="page-note">{activeTabConfig.description}</p>
          </div>

          {renderTabContent(activeTab, {
            phantomInstalled,
            walletConnected,
            walletPublicKey,
          })}
        </div>
      </article>
    </section>
  );
}

export default Subscriptions;
